/**
 * Algolia records may store paths under `levelN` or `lvlN`.
 * Facet / filter attributes use `hierarchicalCategories.levelN` (per index config).
 */
const HIERARCHICAL_FACET_LEVELS = [3, 2, 1] as const;

const RECORD_LEVEL_KEYS: Record<(typeof HIERARCHICAL_FACET_LEVELS)[number], string[]> = {
  3: ['level3', 'lvl3'],
  2: ['level2', 'lvl2'],
  1: ['level1', 'lvl1'],
};

export type HierarchicalCategoriesRecord = Partial<Record<string, string[]>>;

export interface RecommendSourceProduct {
  hierarchicalCategories?: HierarchicalCategoriesRecord | null;
  categories?: string[] | null;
  [attribute: string]: unknown;
}

export interface SimilarItemsQueryParametersInput {
  hierarchicalCategories?: HierarchicalCategoriesRecord | null;
  categories?: string[] | null;
  attributesToFilter?: Array<{ attribute?: string }> | null;
  attributeValues?: Record<string, unknown>;
}

const escapeAlgoliaFilterValue = (value: string): string => {
  const normalized = String(value).trim();
  if (!normalized) return '';
  // Algolia treats unquoted `+` as OR and spaces as token separators — quote those values.
  if (/[:()"\\]|[+\-|]|\||\s/.test(normalized)) {
    return `"${normalized.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  }
  return normalized;
};

const pickBestPath = (paths: string[]): string | undefined => {
  const valid = paths.map((p) => String(p).trim()).filter(Boolean);
  if (!valid.length) return undefined;
  return valid.reduce((best, current) => {
    const bestDepth = best.split('|').length;
    const currentDepth = current.split('|').length;
    return currentDepth >= bestDepth ? current : best;
  });
};

export const getHierarchicalLevelValue = (
  hierarchicalCategories: HierarchicalCategoriesRecord | null | undefined,
  level: (typeof HIERARCHICAL_FACET_LEVELS)[number],
): string | undefined => {
  if (!hierarchicalCategories || typeof hierarchicalCategories !== 'object') {
    return undefined;
  }

  for (const recordKey of RECORD_LEVEL_KEYS[level]) {
    const paths = hierarchicalCategories[recordKey];
    if (Array.isArray(paths) && paths.length > 0) {
      const best = pickBestPath(paths);
      if (best) return best;
    }
  }

  return undefined;
};

export const buildHierarchicalOptionalFilters = (
  hierarchicalCategories: HierarchicalCategoriesRecord | null | undefined,
): string[] => {
  return HIERARCHICAL_FACET_LEVELS.map((level) => {
    const value = getHierarchicalLevelValue(hierarchicalCategories, level);
    if (!value) return '';
    return `hierarchicalCategories.level${level}:${escapeAlgoliaFilterValue(value)}`;
  }).filter(Boolean);
};

/**
 * Uses the leaf category slug (last entry) from the viewed product's categories array.
 */
export const buildCategoriesFilter = (categories: string[] | null | undefined): string => {
  if (!Array.isArray(categories) || categories.length === 0) {
    return '';
  }

  const leafCategory = categories[categories.length - 1];
  if (!leafCategory) return '';

  return `categories:${escapeAlgoliaFilterValue(leafCategory)}`;
};

export const buildRequiredAttributeFilter = (attributeName: string, value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  if (Array.isArray(value) && value.length > 0) {
    const clauses = value
      .filter((val) => val !== undefined && val !== null && val !== '')
      .map((val) => `${attributeName}:${escapeAlgoliaFilterValue(String(val))}`);
    if (!clauses.length) return '';
    return clauses.length > 1 ? `(${clauses.join(' OR ')})` : clauses[0];
  }

  if (!Array.isArray(value)) {
    return `${attributeName}:${escapeAlgoliaFilterValue(String(value))}`;
  }

  return '';
};

export const resolveStudioAttributeValues = (
  attributesToFilter: Array<{ attribute?: string }> | null | undefined,
  sourceProduct: RecommendSourceProduct = {},
  masterVariantAttributes: Record<string, unknown> = {},
): Record<string, unknown> => {
  const attributeNames =
    attributesToFilter?.map((entry) => entry?.attribute?.trim()).filter((name): name is string => Boolean(name)) ?? [];

  return attributeNames.reduce<Record<string, unknown>>((values, attributeName) => {
    const algoliaValue = sourceProduct[attributeName];
    if (algoliaValue !== undefined && algoliaValue !== null && algoliaValue !== '') {
      values[attributeName] = algoliaValue;
      return values;
    }

    const masterVariantValue = masterVariantAttributes[attributeName];
    if (masterVariantValue !== undefined && masterVariantValue !== null && masterVariantValue !== '') {
      values[attributeName] = masterVariantValue;
    }

    return values;
  }, {});
};

export const buildStudioAttributeFilters = (
  attributesToFilter: Array<{ attribute?: string }> | null | undefined,
  attributeValues: Record<string, unknown> = {},
): string[] => {
  const attributeNames =
    attributesToFilter?.map((entry) => entry?.attribute?.trim()).filter((name): name is string => Boolean(name)) ?? [];

  return attributeNames
    .map((attributeName) => buildRequiredAttributeFilter(attributeName, attributeValues[attributeName]))
    .filter(Boolean);
};

export const buildAlgoliaObjectIdFilter = (objectId: string): string =>
  `objectID:${escapeAlgoliaFilterValue(objectId)}`;

const BASE_SOURCE_PRODUCT_ATTRIBUTES = ['hierarchicalCategories', 'categories'] as const;

export const getSourceProductAttributesToRetrieve = (
  attributesToFilter?: Array<{ attribute?: string }> | null,
): string[] => {
  const studioAttributes =
    attributesToFilter?.map((entry) => entry?.attribute?.trim()).filter((name): name is string => Boolean(name)) ?? [];

  return [...new Set([...BASE_SOURCE_PRODUCT_ATTRIBUTES, ...studioAttributes])];
};

export const buildSimilarItemsQueryParameters = ({
  hierarchicalCategories,
  categories,
  attributesToFilter,
  attributeValues = {},
}: SimilarItemsQueryParametersInput): {
  filters?: string;
  optionalFilters?: string[];
} => {
  const requiredFilters = ['isInStock:true'];

  buildStudioAttributeFilters(attributesToFilter, attributeValues).forEach((filter) => {
    requiredFilters.push(filter);
  });

  const categoriesFilter = buildCategoriesFilter(categories);
  if (categoriesFilter) {
    requiredFilters.push(categoriesFilter);
  }

  const optionalFilters = buildHierarchicalOptionalFilters(hierarchicalCategories);

  return {
    filters: requiredFilters.join(' AND '),
    ...(optionalFilters.length > 0 && { optionalFilters }),
  };
};
